<template>
  <div class="h-full">
    <div class="m-auto">
      <div>
        <div>
          <Carousel
            :scroll-to="currentSlide"
            :arrow-mode="$device.isMobile ? 'ALWAYS' : 'NONE'"
            arrow-style="THIN"
            @update-current-pos="activateThumb"
          >
            <div
              v-for="(image, index) in gallery"
              :key="`main-${image.filename}-${index}-${imageOptions.width}-${imageOptions.height}`"
              role="group"
              aria-roledescription="slide"
              :aria-label="image.title || `Gallery Image ${index + 1}`"
              class="w-full"
            >
              <slot v-if="image.gallerySlot" :name="image.gallerySlot"></slot>
              <button
                v-else
                class="w-full cursor-pointer"
                type="button"
                aria-label="Open Large Image"
                @click="loadLightbox(index)"
              >
                <GalleryImage
                  v-if="image.key"
                  :src="
                    getImageUrl(image.key, image.filename, imageOptions.width, imageOptions.height, 80, 'fff') || ''
                  "
                  :width="imageOptions.width"
                  :height="imageOptions.height"
                  :alt="image.title || `Gallery Image ${index + 1}`"
                  :is-lazy="index > 0"
                  class="mx-auto"
                />
              </button>
            </div>
          </Carousel>
        </div>

        <div v-if="showThumbs && gallery.length > 1" class="mt-4 max-w-6xl mx-auto hover:cursor-pointer">
          <Carousel container-class="gap-x-1 mb-2">
            <div
              v-for="(image, index) in gallery"
              :key="`thumb-${image.key}-${index}-${image.filename}`"
              role="group"
              aria-roledescription="slide"
              :aria-label="image.title || `Gallery Thumbnail ${index + 1}`"
            >
              <slot
                v-if="image.thumbSlot"
                :name="image.thumbSlot"
                :activate-thumb="activateThumb"
                :active-thumb="activeThumb"
                :index="index"
              />
              <button
                v-else-if="image.key"
                type="button"
                aria-label="Show Image"
                class="border-2 border-solid w-[140px] rounded"
                :class="[activeThumb === index ? 'border-black' : 'border-gray-light']"
                @click="activateThumb(index)"
              >
                <GalleryImage
                  :src="getImageUrl(image.key, image.filename, 140, 105, 70) || ''"
                  :width="140"
                  :height="105"
                  :alt="image.title || `Gallery Thumbnail ${index + 1}`"
                  :is-lazy="index > ($device.isMobile ? 2 : 5)"
                />
              </button>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  </div>
  <slot :activate-thumb="activateThumb"></slot>
</template>

<script setup lang="ts">
const { $device } = useNuxtApp()
const { getImageUrl } = useUrls()
const { showLightbox } = usePhotoswipeLightbox()
const currentSlide = ref(0)
const activeThumb = ref(0)
const { gallery, showThumbs = true } = defineProps<{
  gallery: ProductLine['gallery']
  showThumbs: boolean
}>()

const imageOptions = computed(() => {
  const width = $device.value.isMobile ? 390 : 1200
  const height = $device.value.isMobile ? 293 : 906

  return { width, height }
})

function loadLightbox(index: number) {
  const items: PhotoswipeItem[] = []

  gallery.forEach((image) => {
    if (!image.key || image.gallerySlot) return

    items.push({
      src: getImageUrl(image.key, image.filename, 1024, 768, 80, 'fff') || '',
      width: 1024,
      height: 768,
      caption: image.title,
    })

    showLightbox(items, index)
  })
}

function activateThumb(index: number) {
  if (index === currentSlide.value) return

  currentSlide.value = index
  activeThumb.value = index
}
</script>
